// ============================================
// verify-email.tsx — R24 e-mail confirmation landing page
// ============================================
//
// The verification e-mail links to
//   {FRONTEND_URL}/verify-email?token=<64-hex>
//
// Opening the link POSTs the token to the backend, which marks the
// address verified and signs the user in (the hook navigates to the
// dashboard on success). Failure states are rendered inline so the
// user can request a fresh link without hunting through e-mail.
//
// This route is intentionally public (no ProtectedRoute /
// GuestOnlyRoute wrapper): a signed-in-but-unverified user does not
// exist yet, and a verified user clicking an old link should see a
// clear "already verified" outcome rather than a redirect loop.

import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, MailCheck, XCircle } from 'lucide-react';
import { useVerifyEmail, useResendVerification } from '@/hooks/use-auth';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

type Status = 'verifying' | 'success' | 'error';

export function VerifyEmailPage() {
    const { t } = useI18n();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const verifyEmail = useVerifyEmail();
    const resendVerification = useResendVerification();

    const [status, setStatus] = useState<Status>(token ? 'verifying' : 'error');
    const [resendEmail, setResendEmail] = useState('');
    const [resent, setResent] = useState(false);
    // StrictMode double-invokes effects; the token is single-use so we
    // must only redeem it once per mount.
    const attemptedRef = useRef(false);

    useEffect(() => {
        if (!token || attemptedRef.current) return;
        attemptedRef.current = true;
        let cancelled = false;
        (async () => {
            try {
                await verifyEmail.mutateAsync(token);
                if (!cancelled) setStatus('success');
            } catch {
                if (!cancelled) setStatus('error');
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    if (status === 'verifying') {
        return (
            <div className="flex min-h-screen items-center justify-center p-4">
                <Card className="w-full max-w-md p-6 text-center space-y-4">
                    <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
                    <p className="text-sm text-foreground-secondary">{t('auth.verifyingEmail')}</p>
                </Card>
            </div>
        );
    }

    if (status === 'success') {
        return (
            <div className="flex min-h-screen items-center justify-center p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <CheckCircle2 className="mx-auto mb-2 h-12 w-12 text-status-success" />
                        <CardTitle>{t('auth.emailVerifiedTitle')}</CardTitle>
                        <CardDescription>{t('auth.emailVerifiedBody')}</CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                        <Button asChild className="w-full">
                            <Link to="/dashboard">{t('auth.goToDashboard')}</Link>
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Error: missing, expired, already-used or unknown token.
    return (
        <div className="flex min-h-screen items-center justify-center p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <XCircle className="mx-auto mb-2 h-12 w-12 text-destructive" />
                    <CardTitle>{t('auth.verifyEmailFailedTitle')}</CardTitle>
                    <CardDescription>{t('auth.verifyEmailFailedBody')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-sm text-foreground-muted">{t('auth.resendVerificationHint')}</p>
                    <Input
                        type="email"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        placeholder={t('auth.emailPlaceholder')}
                        autoComplete="email"
                        data-testid="verify-resend-email"
                    />
                    <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        disabled={resendVerification.isPending || !resendEmail}
                        onClick={() => {
                            resendVerification.mutate(resendEmail, {
                                onSuccess: () => setResent(true),
                            });
                        }}
                        data-testid="verify-resend-submit"
                    >
                        {resendVerification.isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {t('auth.sending')}
                            </>
                        ) : (
                            t('auth.resendVerification')
                        )}
                    </Button>
                    {resent && (
                        <p className="flex items-center justify-center gap-2 text-sm text-status-success">
                            <MailCheck className="h-4 w-4" />
                            {t('auth.verificationResentBody')}
                        </p>
                    )}
                </CardContent>
                <CardFooter className="justify-center">
                    <Link to="/login" className="text-sm text-primary hover:underline font-medium">
                        {t('auth.backToSignIn')}
                    </Link>
                </CardFooter>
            </Card>
        </div>
    );
}
